import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { startOfDay, endOfDay } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  getBalance,
  getCalls,
  getMessages,
  getCustomerAgents,
  getPaymentTransactions,
  getAgentCalendar,
  getAgentCalendarAppointments,
  seedData,
} from '../lib/api';
import { cn, formatCurrency, formatDateTime, formatDuration, formatMinutes } from '../lib/utils';
import {
  normalizeSubscriptionMessagesBalance,
  normalizeSubscriptionMessagesFromAgents,
  normalizeVoiceMinutesQuota,
  normalizeFacebookQuota,
} from '../lib/usageMetrics';
import { toast } from 'sonner';
import {
  Clock,
  CreditCard,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  PhoneCall,
  MessageSquare,
  Timer,
  Receipt,
  Calendar,
  Send,
} from 'lucide-react';
import { QuotaStatCard } from '../components/QuotaStatCard';

/** Same duration display as /calls list rows (minutes_used from API). */
function formatCallRowDuration(call) {
  const minutes = call?.minutes_used ?? call?.minutesUsed;
  if (minutes !== undefined && minutes !== null && `${minutes}`.trim() !== '') {
    const n = Number(minutes);
    if (Number.isFinite(n)) return `${n} min`;
  }
  const seconds = call?.duration_seconds ?? call?.durationSeconds;
  if (seconds !== undefined && seconds !== null && `${seconds}`.trim() !== '') {
    const n = Number(seconds);
    if (Number.isFinite(n)) return formatDuration(n);
  }
  return '—';
}

function parseDashboardAppointmentList(res) {
  const list =
    res.data?.appointments ||
    res.data?.items ||
    res.data?.results ||
    (Array.isArray(res.data) ? res.data : []);
  return Array.isArray(list) ? list : [];
}

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [agents, setAgents] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [mobileUsageOpen, setMobileUsageOpen] = useState(false);
  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isNaN(num) ? fallback : num;
  };

  const getFirstNumeric = (sources, keys, fallback = 0) => {
    for (const source of sources) {
      if (!source) continue;
      for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== '') {
          return toNumber(value, fallback);
        }
      }
    }
    return fallback;
  };

  const normalizeBalance = (raw) => {
    const data = raw?.balance || raw || {};
    const subscription = data?.subscription || null;
    const packageMinutes = getFirstNumeric(
      [data],
      ['package_minutes', 'addon_minutes', 'package_remaining'],
      0
    );
    const totalMinutes = getFirstNumeric([data], ['total_minutes'], packageMinutes);
    const msg = normalizeSubscriptionMessagesBalance(raw);

    return {
      totalMinutes,
      packageMinutes,
      subscriptionRemainingMessages: msg.subscriptionRemainingMessages,
      subscriptionUsedMessages: msg.subscriptionUsedMessages,
      subscriptionTotalMessages: msg.subscriptionTotalMessages,
      subscription: subscription
        ? {
            ...subscription,
            used_message_count: msg.subscriptionUsedMessages,
            total_message_count: msg.subscriptionTotalMessages,
          }
        : null,
    };
  };

  const normalizeAgents = (list) => {
    const source = Array.isArray(list) ? list : [];
    const msg = normalizeSubscriptionMessagesFromAgents(list);
    return {
      subscriptionRemainingMessages: msg.subscriptionRemainingMessages,
      subscriptionUsedMessages: msg.subscriptionUsedMessages,
      subscriptionTotalMessages: msg.subscriptionTotalMessages,
    };
  };

  const paymentBadgeClass = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (['paid', 'fulfilled', 'success', 'complete', 'completed'].includes(normalized)) {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    if (['pending', 'processing', 'requires_action'].includes(normalized)) {
      return 'bg-amber-100 text-amber-700 border-amber-200';
    }
    if (['failed', 'expired', 'canceled', 'cancelled'].includes(normalized)) {
      return 'bg-red-100 text-red-700 border-red-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const extractTransactions = (payload) => {
    if (Array.isArray(payload?.transactions)) return payload.transactions;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadTodayAppointments = async () => {
    try {
      await getAgentCalendar({ suppressGlobalErrorToast: true });
      const from = startOfDay(new Date());
      const to = endOfDay(new Date());
      const aptRes = await getAgentCalendarAppointments(
        { from: from.toISOString(), to: to.toISOString() },
        { suppressGlobalErrorToast: true }
      );
      const list = parseDashboardAppointmentList(aptRes);
      list.sort(
        (a, b) =>
          new Date(a.start_at || a.starts_at || 0).getTime() -
          new Date(b.start_at || b.starts_at || 0).getTime()
      );
      setTodayAppointments(list);
    } catch {
      setTodayAppointments([]);
    }
  };

  const loadData = async () => {
    try {
      // Seed data first (will only run once)
      await seedData().catch(() => {});

      const [balanceRes, callsRes, messagesRes, agentsRes, transactionsRes] = await Promise.all([
        getBalance(),
        getCalls(5, 0),
        getMessages(5, 0).catch(() => ({ data: { messages: [] } })),
        getCustomerAgents({ limit: 200 }).catch(() => ({ data: { agents: [] } })),
        getPaymentTransactions().catch(() => ({ data: { transactions: [] } })),
        loadTodayAppointments(),
      ]);
      setBalance(balanceRes.data);
      setRecentCalls(Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : []);
      setRecentMessages(Array.isArray(messagesRes?.data?.messages) ? messagesRes.data.messages : []);
      setAgents(Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : []);
      const txns = extractTransactions(transactionsRes?.data).slice(0, 5);
      setRecentPayments(txns);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const normalizedBalance = normalizeBalance(balance);
  const normalizedAgents = normalizeAgents(agents);

  const hasAgentSubscriptionData =
    normalizedAgents.subscriptionRemainingMessages > 0 ||
    normalizedAgents.subscriptionUsedMessages > 0 ||
    normalizedAgents.subscriptionTotalMessages > 0;

  const smsRemaining = hasAgentSubscriptionData
    ? normalizedAgents.subscriptionRemainingMessages
    : normalizedBalance.subscriptionRemainingMessages;

  const smsTotal = hasAgentSubscriptionData
    ? normalizedAgents.subscriptionTotalMessages
    : normalizedBalance.subscriptionTotalMessages;

  const voiceQuota = normalizeVoiceMinutesQuota(balance);
  const fbQuota = normalizeFacebookQuota(balance, agents);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back! Here's your voice agent overview.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Button asChild className="bg-teal-600 hover:bg-teal-700" data-testid="buy-minutes-btn">
            <Link to="/buy-minutes">
              <CreditCard className="w-4 h-4 mr-2" />
              Buy Minutes
            </Link>
          </Button>
        </div>
      </div>

      <div className="max-md:mb-2 md:hidden">
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between gap-2 text-left h-auto py-3"
          onClick={() => setMobileUsageOpen((o) => !o)}
          aria-expanded={mobileUsageOpen}
          data-testid="dashboard-mobile-usage-toggle"
        >
          <span className="text-slate-800 font-medium">
            {mobileUsageOpen ? 'Hide' : 'Show'} usage, messages &amp; payments
          </span>
          {mobileUsageOpen ? (
            <ChevronUp className="w-4 h-4 shrink-0 text-slate-500" aria-hidden />
          ) : (
            <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" aria-hidden />
          )}
        </Button>
      </div>

      <div className={cn('space-y-8', !mobileUsageOpen && 'max-md:hidden')}>
        {/* Stats: Minutes, SMS, Social */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <QuotaStatCard
            title="Minutes"
            icon={Clock}
            iconWrapClass="bg-teal-50 text-teal-600"
            remaining={voiceQuota.remaining}
            total={voiceQuota.total}
            formatPrimary={(rem) => formatMinutes(Math.max(0, Math.round(rem)))}
            formatTotalLine={(tot) => `${formatMinutes(Math.max(0, Math.round(tot)))} total available`}
            testId="dashboard-minutes-card"
          />
          <QuotaStatCard
            title="SMS"
            icon={MessageSquare}
            iconWrapClass="bg-green-50 text-green-600"
            remaining={smsRemaining}
            total={smsTotal}
            formatPrimary={(rem) => Math.max(0, Math.round(rem)).toLocaleString()}
            formatTotalLine={(tot) => `${Math.max(0, Math.round(tot)).toLocaleString()} total available`}
            testId="dashboard-sms-card"
          />
          <QuotaStatCard
            title="Social"
            icon={Send}
            iconWrapClass="bg-sky-50 text-sky-600"
            remaining={fbQuota.remaining}
            total={fbQuota.total}
            formatPrimary={(rem) => Math.max(0, Math.round(rem)).toLocaleString()}
            formatTotalLine={(tot) => `${Math.max(0, Math.round(tot)).toLocaleString()} total available`}
            testId="dashboard-social-card"
          />
        </div>
        <div className="md:hidden space-y-8">
          <DashboardRecentMessagesCard recentMessages={recentMessages} />
          <DashboardRecentPaymentsCard
            recentPayments={recentPayments}
            paymentBadgeClass={paymentBadgeClass}
          />
        </div>
      </div>

      {/* Today's appointments (same calendar APIs as /appointments) */}
      <Card className="border-0 shadow-md" data-testid="dashboard-today-appointments">
        <CardHeader className="flex flex-row items-center justify-between pb-2 gap-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600 shrink-0" />
            Today&apos;s appointments
          </CardTitle>
          <Button variant="ghost" size="sm" className="shrink-0" asChild>
            <Link to="/appointments" className="text-teal-600">
              Open calendar
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {todayAppointments.length === 0 ? (
            <div className="text-center py-10">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 text-sm">No appointments scheduled for today.</p>
              <p className="text-slate-400 text-xs mt-1">
                Bookings from your scheduling calendar appear here and on{' '}
                <Link to="/appointments" className="text-teal-600 hover:underline">
                  My appointments
                </Link>
                .
              </p>
            </div>
          ) : (
            <ul className="space-y-2" role="list">
              {todayAppointments.map((apt) => {
                const id = apt.appointment_id || apt.id;
                const start = apt.start_at || apt.starts_at;
                const end = apt.end_at || apt.ends_at;
                const cust = apt.customer || {};
                const name = cust.name || apt.customer_name || 'Customer';
                const phone = cust.phone || apt.customer_phone;
                return (
                  <li
                    key={id || `${start}-${name}`}
                    className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{name}</p>
                      {phone ? <p className="text-xs text-slate-500 truncate">{phone}</p> : null}
                    </div>
                    <p className="text-sm text-slate-700 sm:text-right tabular-nums shrink-0">
                      {formatDateTime(start)} – {formatDateTime(end)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recent Calls */}
      <Card className="border-0 shadow-md" data-testid="recent-calls-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">Recent Calls</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/calls" className="text-teal-600">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentCalls.length === 0 ? (
            <div className="text-center py-12">
              <PhoneCall className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No calls yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Your call logs will appear here once VAPI processes calls
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentCalls.map((call) => (
                <Link
                  key={call.call_id}
                  to={`/calls/${call.call_id}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-teal-100 hover:bg-teal-50/50 transition-all duration-200"
                  data-testid={`call-item-${call.call_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                      <PhoneCall className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        Call #{call.call_id.slice(-6)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatDateTime(call.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Timer className="w-4 h-4" />
                      {formatCallRowDuration(call)}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="max-md:hidden space-y-8">
        <DashboardRecentMessagesCard recentMessages={recentMessages} />
        <DashboardRecentPaymentsCard
          recentPayments={recentPayments}
          paymentBadgeClass={paymentBadgeClass}
        />
      </div>
    </div>
  );
}

function DashboardRecentMessagesCard({ recentMessages }) {
  return (
    <Card className="border-0 shadow-md" data-testid="recent-messages-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold">Recent Messages</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/messages" className="text-teal-600">
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {recentMessages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No messages yet</p>
            <p className="text-sm text-slate-400 mt-1">
              SMS and chat sessions will show up here after customers message your agents
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentMessages.map((row) => {
              const mid = row.message_id || row.messageId || row.id;
              const short = mid && mid.length > 8 ? mid.slice(-6) : mid || '—';
              return (
                <Link
                  key={mid || short}
                  to={`/messages/${encodeURIComponent(mid)}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-teal-100 hover:bg-teal-50/50 transition-all duration-200"
                  data-testid={mid ? `message-item-${mid}` : undefined}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">Message #{short}</p>
                      <p className="text-sm text-slate-500">{formatDateTime(row.created_at)}</p>
                      {row.preview && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{row.preview}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardRecentPaymentsCard({ recentPayments, paymentBadgeClass }) {
  return (
    <Card className="border-0 shadow-md" data-testid="recent-payments-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold">Recent Payments</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/payments/history" className="text-teal-600">
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {recentPayments.length === 0 ? (
          <div className="text-center py-10">
            <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No payment transactions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentPayments.map((txn, idx) => {
              const orderId = txn.order_id || txn.order?.order_id || txn.id;
              return (
                <div
                  key={orderId || idx}
                  className="p-4 rounded-lg border border-slate-100 hover:border-teal-100 hover:bg-teal-50/40 transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900 text-sm break-all">{orderId || 'Order pending'}</p>
                      <p className="text-xs text-slate-500">{txn.created_at ? formatDateTime(txn.created_at) : '-'}</p>
                      <p className="text-sm text-slate-700 mt-1">
                        {txn.amount !== undefined && txn.amount !== null ? formatCurrency(txn.amount) : '-'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={paymentBadgeClass(txn.payment_status)}>
                        payment: {txn.payment_status || '-'}
                      </Badge>
                      <Badge variant="outline" className={paymentBadgeClass(txn.fulfillment_status)}>
                        fulfillment: {txn.fulfillment_status || '-'}
                      </Badge>
                      {orderId && (
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/payment/orders/${orderId}`}>Details</Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-6">
              <Skeleton className="w-12 h-12 rounded-xl mb-4" />
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
